import { useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { EventSummary } from "@/pages/Index";

/** Indexed by Date#getDay, so Sunday first. */
const WEEKDAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];

interface EventCalendarProps {
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
  /** The same list the page is showing, used only to mark days. */
  events?: EventSummary[];
}

const EventCalendar = ({ date, setDate, events = [] }: EventCalendarProps) => {
  // One bad date throws out of the whole modifier and takes the calendar
  // with it, so they are filtered rather than trusted.
  const { mine, scheduled } = useMemo(() => {
    const mine: Date[] = [];
    const scheduled: Date[] = [];
    for (const event of events) {
      const day = new Date(event.startTime);
      if (isNaN(day.getTime())) continue;
      (event.isUserRegistered ? mine : scheduled).push(day);
    }
    return { mine, scheduled };
  }, [events]);

  return (
    <Card className="p-0">
      <div className="border-b border-border px-5 py-4">
        <h2 className="font-sans text-[15px] font-bold uppercase tracking-eyebrow text-foreground">
          Calendar
        </h2>
      </div>

      <Calendar
        mode="single"
        selected={date}
        onSelect={setDate}
        // A day you are working is also a day with an event, and the later
        // class wins, so "mine" comes second.
        modifiers={{ scheduled, mine }}
        modifiersClassNames={{
          scheduled: "day-dot day-dot-scheduled",
          mine: "day-dot day-dot-mine",
        }}
        className="w-full p-4"
        // The shared calendar lays a month out as flex rows of fixed 36px
        // cells, which is right for a popover and leaves a column of dead
        // space in a card this wide. Overridden here rather than in the shared
        // component, because the form date pickers are the narrow case.
        classNames={{
          month: "w-full space-y-4",
          table: "w-full border-collapse",
          head_row: "grid grid-cols-7",
          head_cell:
            "w-full rounded-md font-normal text-[0.8rem] text-muted-foreground",
          row: "mt-2 grid grid-cols-7",
          // No `[&:has([aria-selected])]:bg-accent` here. The shared
          // calendar tints the cell as well as the day, which is invisible in
          // a popover where the two are the same width and, in a card this
          // wide, paints a band around the day so it looks selected twice.
          cell: "relative h-9 w-full p-0 text-center text-sm focus-within:relative focus-within:z-20 [&>button]:mx-auto",
        }}
        formatters={{
          formatWeekdayName: (day) => WEEKDAY_INITIALS[day.getDay()],
        }}
      />

      {/* The swatches have to be drawn as the markers are, or the legend is
          a second thing to learn. */}
      <div className="flex items-center gap-4 border-t border-border px-5 py-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full border-[1.5px] border-primary bg-primary" />
          You are working
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full border-[1.5px] border-muted-foreground/85" />
          Event scheduled
        </span>
      </div>
    </Card>
  );
};

export default EventCalendar;
